import React, { useRef, useEffect, useState } from 'react'
import DraggableCustom from '../DraggableCustom/DraggableCustom'
import { calcTravelledDistance } from '../../utils/CalculateUtils'
import { Legends, ParamsEvent } from '../../types'
import styled from 'styled-components'
import { useSize } from 'ahooks'
import i18n from 'live/services/i18n'
import { deepFind } from '../../utils/JsonUtils'

type IProps = {
    title?: string
    active?: boolean
    values: BarValue[]
    paramsEvents?: ParamsEvent
    cursorPosition: { x: number; y: number }
    handleDraggableOnDrag(event, ui, rightBound): void
    handleDraggableOnStop(event, ui, rightBound): void
}

export type BarValue = {
    color: string
    distance: number
}

type LegendsProps = {
    flexEnd?: boolean
    event: ParamsEvent
    values: Legends[]
}

const Legend = ({ flexEnd = false, values, event }: LegendsProps): JSX.Element => {
    if (!values) return <></>

    return (
        <BarLegend flexEnd={flexEnd}>
            {values?.map(({ title, value, field, color, html }, index) => {
                return (
                    <div className="bar-legends-item" key={index}>
                        {html ? (
                            <>{}</>
                        ) : (
                            <>
                                {color && <div style={{ background: color }} className="bar-legends-icon" />}

                                <span className="bar-legends-title">{i18n(title)}:</span>
                                <span className="bar-legends-value">{field ? deepFind(event, field) : value}</span>
                            </>
                        )}
                    </div>
                )
            })}
        </BarLegend>
    )
}

const Bar = ({
    title,
    active = false,
    values,
    paramsEvents,
    cursorPosition = { x: 0, y: 0 },
    handleDraggableOnStop,
    handleDraggableOnDrag
}: IProps): JSX.Element => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const size = useSize(document.getElementById('drill-down-bar'))

    useEffect(() => {
        initCanva()
    }, [size?.width, values]) // eslint-disable-line react-hooks/exhaustive-deps

    const initCanva = (): void => {
        if (canvasRef && canvasRef.current) {
            const canvas = canvasRef.current
            const context = canvas.getContext('2d')
            draw(context, values)
        }
    }

    const draw = (ctx, events: BarValue[]): void => {
        if (!ctx) return

        const barWidth = size?.width

        ctx.clearRect(0, 0, barWidth, 26)

        const travelledDistance = calcTravelledDistance(events)
        let x = 0
        let acumulateDistance = 0

        if (events.length === 0) {
            ctx.fillStyle = '#E5E5E5'
            ctx.fillRect(x, 0, barWidth, 150)
        } else {
            for (let index = 1; index < events.length; index++) {
                const event = events[index]
                const { distance = 0, color } = event

                acumulateDistance += distance
                const width = (acumulateDistance * barWidth) / travelledDistance

                ctx.fillStyle = color
                ctx.fillRect(x, 0, width, 150)

                x = width
            }
        }
    }

    const [width, setWidth] = useState<number | null>(size?.width)
    const previousWidthRef = useRef<number | null>(size?.width)

    useEffect(() => {
        setWidth(prev => {
            if (!previousWidthRef.current) previousWidthRef.current = prev

            return size?.width
        })
    }, [size?.width])

    useEffect(() => {
        if (!isNaN(previousWidthRef.current)) {
            previousWidthRef.current = width
        }
    }, [cursorPosition]) // eslint-disable-line react-hooks/exhaustive-deps

    let newCursorPosition = { ...cursorPosition }
    if (
        Number.isFinite(width) &&
        Number.isFinite(previousWidthRef.current) &&
        Number.isFinite(cursorPosition.x) &&
        previousWidthRef.current !== 0
    ) {
        newCursorPosition = {
            ...cursorPosition,
            x: (cursorPosition.x * width) / previousWidthRef.current
        }
    }


    return (
        <WrapperBar>
            <BarContent>
                <WrapperLegends>
                    <div className="bar-title">
                        <div>{title}</div>
                        {active && <OnMap>{i18n('On map')}</OnMap>}
                    </div>
                    <Legend event={paramsEvents} flexEnd={true} values={paramsEvents?.legends?.[title]} />
                </WrapperLegends>

                <div id="drill-down-bar" className="bar">
                    <canvas data-testid="test-bar" ref={canvasRef} width={size?.width} height={12} />

                    <DraggableCustom
                        rightBound={size?.width}
                        position={newCursorPosition}
                        _handleDraggableOnDrag={handleDraggableOnDrag}
                        _handleDraggableOnStop={handleDraggableOnStop}
                    />
                </div>

                <WrapperLegends>
                    <Legend event={paramsEvents} values={paramsEvents?.sublegends?.[title]} />
                </WrapperLegends>
            </BarContent>
        </WrapperBar>
    )
}

export default Bar

const OnMap = styled.div`
    margin-left: 8px;
    padding: 1px 5px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme['gray-600']};
    color: ${({ theme }) => theme['gray-600']};
`

const WrapperBar = styled.div`
    display: flex;
    margin-bottom: 16px;

    * {
        box-sizing: border-box;
    }
`

const BarContent = styled.div`
    flex: 1;
    overflow: hidden;
    padding: 0px 16px;

    .bar {
        margin: 0;
        padding: 0;
        height: 22px;
    }
`

const WrapperLegends = styled.div`
    display: flex;
    padding-bottom: 4px;

    .bar-title {
        padding-bottom: 3px;
        display: flex;
        align-items: end;
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme['gray-800']};
    }
`

const BarLegend = styled.div`
    flex: 1;
    display: flex;
    font-size: 11px;
    justify-content: ${({ flexEnd }) => (flexEnd ? 'flex-end' : 'flex-start')};

    .bar-legends-item {
        margin-left: 28px;
        display: flex;
        align-items: center;

        &:first-of-type {
            margin-left: 0px;
        }
    }

    .bar-legends-icon {
        width: 8px;
        height: 8px;
        margin-right: 4px;
        border-radius: 16px;
    }

    .bar-legends-title {
        font-size: 13px;
        color: ${({ theme }) => theme['gray-600']};
    }

    .bar-legends-value {
        margin-left: 4px;
        font-weight: 600;
        font-size: 13px;
        color: ${({ theme }) => theme['gray-700']};
    }
`
